import { useCallback, useRef } from 'react';
import { ui as ShakaUI } from 'shaka-player/dist/shaka-player.ui';
import { SkipBackIcon, SkipForwardIcon, BigPlayIcon } from '../icons';
import { renderIcon } from '../utils/renderIcon';

interface ChromecastConfig {
  receiverApplicationId?: string;
}

interface IconSizes {
  skipButtons?: number;
  bigPlayButton?: number;
}

// Custom Skip Back Button Element
class SkipBackButton {
  private button_: HTMLButtonElement;
  private parent: HTMLElement;
  private controls: any;
  private eventManager: any;

  constructor(parent: HTMLElement, controls: any, onSkipBack?: (newTime: number) => void, iconSize = 24) {
    this.parent = parent;
    this.controls = controls;
    this.eventManager = { listen: (element: HTMLElement, event: string, handler: () => void) => {
      element.addEventListener(event, handler);
    }};

    this.button_ = document.createElement('button');
    this.button_.className = 'shaka-button motto-native-skip-button';
    this.button_.innerHTML = renderIcon(SkipBackIcon, { size: iconSize });
    this.button_.title = 'Skip back 15 seconds';
    this.button_.setAttribute('aria-label', 'Skip back 15 seconds');
    
    this.parent.appendChild(this.button_);

    this.eventManager.listen(this.button_, 'click', () => {
      const video = this.controls.getVideo();
      if (video) {
        const newTime = Math.max(0, video.currentTime - 15);
        video.currentTime = newTime;
        onSkipBack?.(newTime);
      }
    });
  }

  // Shaka UI will call `release` when the controls are destroyed.
  // Provide a no-op implementation to avoid TypeErrors.
  public release() {
    /* Optionally detach listeners / clean up DOM here */
  }
}

// Custom Skip Forward Button Element  
class SkipForwardButton {
  private button_: HTMLButtonElement;
  private parent: HTMLElement;
  private controls: any;
  private eventManager: any;

  constructor(parent: HTMLElement, controls: any, onSkipForward?: (newTime: number) => void, iconSize = 24) {
    this.parent = parent;
    this.controls = controls;
    this.eventManager = { listen: (element: HTMLElement, event: string, handler: () => void) => {
      element.addEventListener(event, handler);
    }};

    this.button_ = document.createElement('button');
    this.button_.className = 'shaka-button motto-native-skip-button';
    this.button_.innerHTML = renderIcon(SkipForwardIcon, { size: iconSize });
    this.button_.title = 'Skip forward 15 seconds';
    this.button_.setAttribute('aria-label', 'Skip forward 15 seconds');
    
    this.parent.appendChild(this.button_);

    this.eventManager.listen(this.button_, 'click', () => {
      const video = this.controls.getVideo();
      if (video) {
        const newTime = Math.min(video.duration || 0, video.currentTime + 15);
        video.currentTime = newTime;
        onSkipForward?.(newTime);
      }
    });
  }

  public release() {
    /* Clean-up logic if needed */
  }
}

// Factory classes for the custom elements
class SkipBackButtonFactory {
  constructor(private onSkipBack?: (newTime: number) => void, private iconSize?: number) {}
  
  create(rootElement: HTMLElement, controls: any) {
    return new SkipBackButton(rootElement, controls, this.onSkipBack, this.iconSize);
  }
}

class SkipForwardButtonFactory {
  constructor(private onSkipForward?: (newTime: number) => void, private iconSize?: number) {}
  
  create(rootElement: HTMLElement, controls: any) {
    return new SkipForwardButton(rootElement, controls, this.onSkipForward, this.iconSize);
  }
}

interface SeekbarColors {
  base?: string;
  buffered?: string;
  played?: string;
}

export const useShakaUI = (
  playerRef: React.RefObject<any>,
  containerRef: React.RefObject<HTMLDivElement>,
  videoRef: React.RefObject<HTMLVideoElement>,
  controls: boolean,
  chromecastConfig?: ChromecastConfig,
  seekbarColors?: SeekbarColors,
  onSkipBack?: (newTime: number) => void,
  onSkipForward?: (newTime: number) => void,
  iconSizes?: IconSizes,
  locale: string = 'en'
) => {
  const uiRef = useRef<any>(null);
  const registeredElements = useRef<Set<string>>(new Set());

  const initializeUI = useCallback(async () => {
    if (!controls || !containerRef.current || !playerRef.current || !videoRef.current) {
      return null;
    }

    // Register custom elements if not already registered
    if (!registeredElements.current.has('skip_back_button')) {
      ShakaUI.Controls.registerElement('skip_back_button', new SkipBackButtonFactory(onSkipBack, iconSizes?.skipButtons));
      registeredElements.current.add('skip_back_button');
    }
    
    if (!registeredElements.current.has('skip_forward_button')) {
      ShakaUI.Controls.registerElement('skip_forward_button', new SkipForwardButtonFactory(onSkipForward, iconSizes?.skipButtons));
      registeredElements.current.add('skip_forward_button');
    }

    // Create UI with Shaka Player controls using default configuration
    const ui = new ShakaUI.Overlay(playerRef.current, containerRef.current, videoRef.current);
    uiRef.current = ui;
    
    // Configure localization if locale is not 'en'
    if (locale !== 'en') {
      try {
        ui.getControls().getLocalization().changeLocale([locale])
        console.log(`Set locale to '${locale}'`);

      } catch (error) {
        console.warn(`Failed to set locale to '${locale}', falling back to 'en':`, error);
      }
    }

    // Detect mobile to conditionally include volume control
    const isMobile = window.innerWidth <= 767;
    
    const controlPanelElements = [
      ...(isMobile ? [] : ['skip_back_button']), 
      ...(isMobile ? [] : ['play_pause']), 
      ...(isMobile ? [] : ['skip_forward_button']), 
      'mute',
      ...(isMobile ? [] : ['volume']), // Only include volume on desktop
      'time_and_duration',
      'spacer',
      'fullscreen',
      'cast', // Always show cast button
      'overflow_menu',
    ];

    // Configure UI settings with custom control panel layout
    const uiConfig: any = {
      seekBarColors: {
        base: seekbarColors?.base || 'rgba(255, 255, 255, 0.3)',     // Unbuffered track
        buffered: seekbarColors?.buffered || 'rgba(255, 255, 255, 0.5)', // Buffered but not played
        played: seekbarColors?.played || '#ffffff',                     // Progress/played portion (white)
      },
      controlPanelElements: controlPanelElements,
      addBigPlayButton: isMobile,
      // For now, Chromecast via Shaka must use the Motto receiver that runs on the legacy cast SDK (2.0), because
      // Shaka does not support the newer Cast SDK (3.0+) in its native integration.
      castReceiverAppId: 'EC900D4D',
      castAndroidReceiverCompatible: true, // Enable Android TV compatibility
      overflowMenuButtons: [
        'quality',
        'captions',
        'picture_in_picture',
        'playback_rate'
      ]
    };

    ui.configure(uiConfig);

    // Custom SVG replacement for big play button on mobile
    if (isMobile) {
      const customizeBigPlayButton = () => {
        const bigPlayButton = containerRef.current?.querySelector('.shaka-big-play-button');
        if (bigPlayButton && !bigPlayButton.hasAttribute('data-customized')) {
          // Use custom size if provided, otherwise use default
          const buttonSize = iconSizes?.bigPlayButton || 40;
          
          // Clear existing content and add custom SVG
          bigPlayButton.innerHTML = renderIcon(BigPlayIcon, { size: buttonSize });
          
          // Mark as customized to avoid re-processing
          bigPlayButton.setAttribute('data-customized', 'true');
          
          // Ensure the button is properly styled
          const buttonElement = bigPlayButton as HTMLElement;
          buttonElement.style.display = 'flex';
          buttonElement.style.alignItems = 'center';
          buttonElement.style.justifyContent = 'center';
        }
      };

      // Apply customization
      setTimeout(customizeBigPlayButton, 100);

      // Watch for changes and reapply customization
      const observer = new MutationObserver(() => {
        customizeBigPlayButton();
      });

      if (containerRef.current) {
        observer.observe(containerRef.current, {
          childList: true,
          subtree: true,
          attributes: false
        });
      }
    }

    // Seekbar styling is now handled by CSS with proper background

    return ui;
  }, [controls, containerRef, playerRef, videoRef, chromecastConfig, seekbarColors, onSkipBack, onSkipForward, iconSizes, locale]);

  const destroyUI = useCallback(async () => {
    const uiInstance = uiRef.current;
    if (uiInstance) {
      try {
        await uiInstance.destroy();
      } catch (error) {
        console.error('Error destroying UI:', error);
      } finally {
        if (uiRef.current === uiInstance) {
          uiRef.current = null;
        }
      }
    }
  }, []);

  return {
    uiRef,
    initializeUI,
    destroyUI
  };
}; 